import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Mail,
  Plus,
  Send,
  FileText,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Edit,
  Trash2,
  Eye,
  Filter,
  RefreshCw,
  Bell,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminEmailCampaigns = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [reminderRules, setReminderRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Template modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body_html: '',
    template_type: 'general'
  });
  
  // Campaign modal state
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    template_id: '',
    customer_types: [],
    vehicle_types: [],
    last_visit_days: ''
  });
  const [customerPreview, setCustomerPreview] = useState(null);
  
  // Reminder rule modal state
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    rule_type: 'months_since_service',
    trigger_value: 6,
    template_id: '',
    vehicle_types: [],
    is_active: true
  });
  
  // Send confirmation modal
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [campaignToSend, setCampaignToSend] = useState(null);
  
  const { toast } = useToast();
  const { token } = useAuth();
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, campaignsRes, rulesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/email/templates`, { headers }),
        axios.get(`${BACKEND_URL}/api/email/campaigns`, { headers }),
        axios.get(`${BACKEND_URL}/api/email/reminder-rules`, { headers })
      ]);
      setTemplates(templatesRes.data);
      setCampaigns(campaignsRes.data);
      setReminderRules(rulesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Feil', description: 'Kunne ikke hente data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Template handlers
  const handleTemplateFieldChange = useCallback((field, value) => {
    setTemplateForm(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        subject: template.subject,
        body_html: template.body_html,
        template_type: template.template_type
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        subject: '',
        body_html: getDefaultTemplateHtml(),
        template_type: 'general'
      });
    }
    setTemplateModalOpen(true);
  };
  
  const handleSaveTemplate = async () => {
    setActionLoading(true);
    try {
      if (editingTemplate) {
        await axios.put(
          `${BACKEND_URL}/api/email/templates/${editingTemplate.id}`,
          templateForm,
          { headers }
        );
        toast({ title: 'Suksess', description: 'Mal oppdatert' });
      } else {
        await axios.post(`${BACKEND_URL}/api/email/templates`, templateForm, { headers });
        toast({ title: 'Suksess', description: 'Mal opprettet' });
      }
      setTemplateModalOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke lagre mal', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne malen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/email/templates/${templateId}`, { headers });
      toast({ title: 'Slettet', description: 'Mal slettet' });
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke slette mal', variant: 'destructive' });
    }
  };
  
  // Campaign handlers
  const handleCampaignFieldChange = useCallback((field, value) => {
    setCampaignForm(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const previewCustomers = async () => {
    try {
      const filters = {
        customer_types: campaignForm.customer_types.length > 0 ? campaignForm.customer_types : undefined,
        vehicle_types: campaignForm.vehicle_types.length > 0 ? campaignForm.vehicle_types : undefined
      };
      
      if (campaignForm.last_visit_days) {
        const days = parseInt(campaignForm.last_visit_days);
        const date = new Date();
        date.setDate(date.getDate() - days);
        filters.last_visit_before = date.toISOString();
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/email/customers/preview`,
        filters,
        { headers }
      );
      setCustomerPreview(response.data);
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke hente kundefilter', variant: 'destructive' });
    }
  };
  
  const handleCreateCampaign = async () => {
    if (!campaignForm.name || !campaignForm.template_id) {
      toast({ title: 'Feil', description: 'Fyll ut alle påkrevde felt', variant: 'destructive' });
      return;
    }
    
    setActionLoading(true);
    try {
      const filters = {
        customer_types: campaignForm.customer_types.length > 0 ? campaignForm.customer_types : undefined,
        vehicle_types: campaignForm.vehicle_types.length > 0 ? campaignForm.vehicle_types : undefined
      };
      
      if (campaignForm.last_visit_days) {
        const days = parseInt(campaignForm.last_visit_days);
        const date = new Date();
        date.setDate(date.getDate() - days);
        filters.last_visit_before = date.toISOString();
      }
      
      await axios.post(`${BACKEND_URL}/api/email/campaigns`, {
        name: campaignForm.name,
        template_id: campaignForm.template_id,
        recipient_filter: filters
      }, { headers });
      
      toast({ title: 'Suksess', description: 'Kampanje opprettet' });
      setCampaignModalOpen(false);
      setCampaignForm({
        name: '',
        template_id: '',
        customer_types: [],
        vehicle_types: [],
        last_visit_days: ''
      });
      setCustomerPreview(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: error.response?.data?.detail || 'Kunne ikke opprette kampanje', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleSendCampaign = async () => {
    if (!campaignToSend) return;
    
    setActionLoading(true);
    try {
      const result = await axios.post(
        `${BACKEND_URL}/api/email/campaigns/${campaignToSend.id}/send`,
        {},
        { headers }
      );
      
      toast({
        title: 'Kampanje sendt!',
        description: `${result.data.sent} e-poster sendt, ${result.data.failed} feilet`
      });
      setSendModalOpen(false);
      setCampaignToSend(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: error.response?.data?.detail || 'Kunne ikke sende kampanje', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Reminder rule handlers
  const handleRuleFieldChange = useCallback((field, value) => {
    setRuleForm(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const openRuleModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        name: rule.name,
        rule_type: rule.rule_type,
        trigger_value: rule.trigger_value,
        template_id: rule.template_id,
        vehicle_types: rule.vehicle_types || [],
        is_active: rule.is_active
      });
    } else {
      setEditingRule(null);
      setRuleForm({
        name: '',
        rule_type: 'months_since_service',
        trigger_value: 6,
        template_id: '',
        vehicle_types: [],
        is_active: true
      });
    }
    setRuleModalOpen(true);
  };
  
  const handleSaveRule = async () => {
    setActionLoading(true);
    try {
      if (editingRule) {
        await axios.put(
          `${BACKEND_URL}/api/email/reminder-rules/${editingRule.id}`,
          ruleForm,
          { headers }
        );
        toast({ title: 'Suksess', description: 'Regel oppdatert' });
      } else {
        await axios.post(`${BACKEND_URL}/api/email/reminder-rules`, ruleForm, { headers });
        toast({ title: 'Suksess', description: 'Regel opprettet' });
      }
      setRuleModalOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke lagre regel', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne regelen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/email/reminder-rules/${ruleId}`, { headers });
      toast({ title: 'Slettet', description: 'Regel slettet' });
      fetchData();
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke slette regel', variant: 'destructive' });
    }
  };
  
  const handleCheckReminders = async () => {
    setActionLoading(true);
    try {
      const result = await axios.post(
        `${BACKEND_URL}/api/email/reminder-rules/check`,
        {},
        { headers }
      );
      toast({
        title: 'Påminnelser sjekket',
        description: `${result.data.reminders_sent} påminnelser sendt`
      });
    } catch (error) {
      toast({ title: 'Feil', description: 'Kunne ikke sjekke påminnelser', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Helper functions
  const getDefaultTemplateHtml = () => `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #dc2626 0%, #f59e0b 100%); 
              color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Sand-tec AS</h1>
    </div>
    <div class="content">
      <p>Hei {{name}},</p>
      <p>Din melding her...</p>
    </div>
    <div class="footer">
      <p>Med vennlig hilsen,<br><strong>Sand-tec AS Team</strong></p>
    </div>
  </div>
</body>
</html>`;
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft': return <Badge className="bg-gray-500">Utkast</Badge>;
      case 'scheduled': return <Badge className="bg-blue-500">Planlagt</Badge>;
      case 'sending': return <Badge className="bg-yellow-500">Sender...</Badge>;
      case 'sent': return <Badge className="bg-green-500">Sendt</Badge>;
      case 'failed': return <Badge className="bg-red-500">Feilet</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };
  
  const getTemplateTypeBadge = (type) => {
    switch (type) {
      case 'campaign': return <Badge className="bg-purple-500">Kampanje</Badge>;
      case 'reminder': return <Badge className="bg-orange-500">Påminnelse</Badge>;
      case 'info': return <Badge className="bg-blue-500">Info</Badge>;
      default: return <Badge className="bg-gray-500">Generell</Badge>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="email-campaigns-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="w-7 h-7 text-yellow-400" />
            E-postkampanjer
          </h1>
          <p className="text-gray-400 mt-1">Send e-poster til kunder og sett opp automatiske påminnelser</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-gray-600 text-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Oppdater
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-800">
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-yellow-600">
            <Send className="w-4 h-4 mr-2" />
            Kampanjer
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-yellow-600">
            <FileText className="w-4 h-4 mr-2" />
            Maler
          </TabsTrigger>
          <TabsTrigger value="reminders" className="data-[state=active]:bg-yellow-600">
            <Bell className="w-4 h-4 mr-2" />
            Påminnelser
          </TabsTrigger>
        </TabsList>
        
        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">E-postkampanjer</h2>
            <Button onClick={() => setCampaignModalOpen(true)} className="bg-yellow-600 hover:bg-yellow-700">
              <Plus className="w-4 h-4 mr-2" />
              Ny kampanje
            </Button>
          </div>
          
          <div className="grid gap-4">
            {campaigns.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen kampanjer ennå</p>
                  <p className="text-gray-500 text-sm mt-1">Opprett din første kampanje for å nå kundene dine</p>
                </CardContent>
              </Card>
            ) : (
              campaigns.map(campaign => (
                <Card key={campaign.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-medium">{campaign.name}</h3>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-gray-400 text-sm mt-1">
                          Mal: {campaign.template_name} | {campaign.recipient_count} mottakere
                        </p>
                        {campaign.sent_at && (
                          <p className="text-gray-500 text-xs mt-1">
                            Sendt: {new Date(campaign.sent_at).toLocaleString('nb-NO')}
                          </p>
                        )}
                        {campaign.stats && campaign.status === 'sent' && (
                          <div className="flex gap-4 mt-2 text-sm">
                            <span className="text-green-400">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              {campaign.stats.sent} sendt
                            </span>
                            {campaign.stats.failed > 0 && (
                              <span className="text-red-400">
                                <XCircle className="w-4 h-4 inline mr-1" />
                                {campaign.stats.failed} feilet
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {campaign.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setCampaignToSend(campaign);
                              setSendModalOpen(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">E-postmaler</h2>
            <Button onClick={() => openTemplateModal()} className="bg-yellow-600 hover:bg-yellow-700">
              <Plus className="w-4 h-4 mr-2" />
              Ny mal
            </Button>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700 col-span-full">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen maler ennå</p>
                  <p className="text-gray-500 text-sm mt-1">Opprett din første e-postmal</p>
                </CardContent>
              </Card>
            ) : (
              templates.map(template => (
                <Card key={template.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-medium">{template.name}</h3>
                        <p className="text-gray-400 text-sm mt-1">{template.subject}</p>
                        <div className="mt-2">
                          {getTemplateTypeBadge(template.template_type)}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openTemplateModal(template)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
        
        {/* Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-white">Automatiske påminnelser</h2>
              <p className="text-gray-400 text-sm">Regler for automatisk service-påminnelse</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCheckReminders}
                variant="outline"
                className="border-gray-600 text-gray-300"
                disabled={actionLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                Kjør sjekk nå
              </Button>
              <Button onClick={() => openRuleModal()} className="bg-yellow-600 hover:bg-yellow-700">
                <Plus className="w-4 h-4 mr-2" />
                Ny regel
              </Button>
            </div>
          </div>
          
          {/* Cron Info Box */}
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="text-white font-medium">Automatisk kjøring</h4>
                  <p className="text-gray-300 text-sm mt-1">
                    For å sende påminnelser automatisk, sett opp en ekstern scheduler (f.eks. cron-job.org) som kaller:
                  </p>
                  <code className="block mt-2 p-2 bg-gray-900 rounded text-xs text-green-400 break-all">
                    POST {window.location.origin}/api/cron/send-reminders?api_key=DIN_API_NØKKEL
                  </code>
                  <p className="text-gray-500 text-xs mt-2">
                    API-nøkkelen finnes i backend/.env som CRON_API_KEY. Anbefalt: Daglig kl. 08:00.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid gap-4">
            {reminderRules.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Bell className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen påminnelsesregler ennå</p>
                  <p className="text-gray-500 text-sm mt-1">Opprett regler for automatiske service-påminnelser</p>
                </CardContent>
              </Card>
            ) : (
              reminderRules.map(rule => (
                <Card key={rule.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-medium">{rule.name}</h3>
                          <Badge className={rule.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                            {rule.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                        <p className="text-gray-400 text-sm mt-1">
                          {rule.rule_type === 'months_since_service' 
                            ? `${rule.trigger_value} måneder siden siste service`
                            : rule.rule_type === 'days_before_service'
                            ? `${rule.trigger_value} dager før neste service`
                            : rule.rule_type}
                        </p>
                        {rule.vehicle_types && rule.vehicle_types.length > 0 && (
                          <p className="text-gray-500 text-xs mt-1">
                            Kjøretøy: {rule.vehicle_types.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRuleModal(rule)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Template Modal */}
      <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-yellow-400" />
              {editingTemplate ? 'Rediger mal' : 'Ny e-postmal'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Bruk {`{{name}}`}, {`{{email}}`}, {`{{phone}}`} etc. for personalisering
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Navn på mal</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => handleTemplateFieldChange('name', e.target.value)}
                  placeholder="F.eks. Service-påminnelse"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Type</Label>
                <Select
                  value={templateForm.template_type}
                  onValueChange={(value) => handleTemplateFieldChange('template_type', value)}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="general" className="text-white">Generell</SelectItem>
                    <SelectItem value="campaign" className="text-white">Kampanje/Tilbud</SelectItem>
                    <SelectItem value="reminder" className="text-white">Påminnelse</SelectItem>
                    <SelectItem value="info" className="text-white">Informasjon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-gray-300">Emne</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => handleTemplateFieldChange('subject', e.target.value)}
                placeholder="E-postens emne"
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">HTML-innhold</Label>
              <Textarea
                value={templateForm.body_html}
                onChange={(e) => handleTemplateFieldChange('body_html', e.target.value)}
                placeholder="HTML-kode for e-posten"
                className="bg-gray-800 border-gray-600 text-white mt-1 font-mono text-sm"
                rows={12}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleSaveTemplate} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Campaign Modal */}
      <Dialog open={campaignModalOpen} onOpenChange={setCampaignModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-yellow-400" />
              Ny e-postkampanje
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Velg mottakere og mal for kampanjen
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Kampanjenavn</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => handleCampaignFieldChange('name', e.target.value)}
                placeholder="F.eks. Vårkampanje 2025"
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">E-postmal</Label>
              <Select
                value={campaignForm.template_id}
                onValueChange={(value) => handleCampaignFieldChange('template_id', value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="Velg mal" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Målgruppe
              </h4>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-300 text-sm">Kundetype</Label>
                  <div className="flex gap-4 mt-2">
                    {['private', 'business', 'fleet'].map(type => (
                      <label key={type} className="flex items-center gap-2 text-gray-300">
                        <Checkbox
                          checked={campaignForm.customer_types.includes(type)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleCampaignFieldChange('customer_types', [...campaignForm.customer_types, type]);
                            } else {
                              handleCampaignFieldChange('customer_types', campaignForm.customer_types.filter(t => t !== type));
                            }
                          }}
                        />
                        {type === 'private' ? 'Privat' : type === 'business' ? 'Bedrift' : 'Flåte'}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-300 text-sm">Kjøretøytype</Label>
                  <div className="flex gap-4 mt-2">
                    {['personbil', 'tungbil', 'tilhenger'].map(type => (
                      <label key={type} className="flex items-center gap-2 text-gray-300">
                        <Checkbox
                          checked={campaignForm.vehicle_types.includes(type)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleCampaignFieldChange('vehicle_types', [...campaignForm.vehicle_types, type]);
                            } else {
                              handleCampaignFieldChange('vehicle_types', campaignForm.vehicle_types.filter(t => t !== type));
                            }
                          }}
                        />
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-300 text-sm">Ikke besøkt siste X dager</Label>
                  <Input
                    type="number"
                    value={campaignForm.last_visit_days}
                    onChange={(e) => handleCampaignFieldChange('last_visit_days', e.target.value)}
                    placeholder="F.eks. 90"
                    className="bg-gray-800 border-gray-600 text-white mt-1 w-32"
                  />
                </div>
              </div>
              
              <Button
                onClick={previewCustomers}
                variant="outline"
                className="mt-4 border-gray-600 text-gray-300"
              >
                <Eye className="w-4 h-4 mr-2" />
                Forhåndsvis mottakere
              </Button>
              
              {customerPreview && (
                <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                  <p className="text-white font-medium">
                    <Users className="w-4 h-4 inline mr-2" />
                    {customerPreview.count} kunder funnet
                  </p>
                  {customerPreview.customers.length > 0 && (
                    <div className="mt-2 text-sm text-gray-400">
                      <p>Forhåndsvisning:</p>
                      <ul className="mt-1 space-y-1">
                        {customerPreview.customers.slice(0, 5).map((c, i) => (
                          <li key={i}>{c.name} - {c.email}</li>
                        ))}
                        {customerPreview.count > 5 && <li>...og {customerPreview.count - 5} flere</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleCreateCampaign} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Opprett kampanje'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reminder Rule Modal */}
      <Dialog open={ruleModalOpen} onOpenChange={setRuleModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-400" />
              {editingRule ? 'Rediger påminnelsesregel' : 'Ny påminnelsesregel'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Regelnavn</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => handleRuleFieldChange('name', e.target.value)}
                placeholder="F.eks. 6 måneders service-påminnelse"
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Regeltype</Label>
              <Select
                value={ruleForm.rule_type}
                onValueChange={(value) => handleRuleFieldChange('rule_type', value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="months_since_service" className="text-white">Måneder siden siste service</SelectItem>
                  <SelectItem value="days_before_service" className="text-white">Dager før neste planlagte service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-300">
                {ruleForm.rule_type === 'months_since_service' ? 'Antall måneder' : 'Antall dager'}
              </Label>
              <Input
                type="number"
                value={ruleForm.trigger_value}
                onChange={(e) => handleRuleFieldChange('trigger_value', parseInt(e.target.value) || 0)}
                className="bg-gray-800 border-gray-600 text-white mt-1 w-32"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">E-postmal</Label>
              <Select
                value={ruleForm.template_id}
                onValueChange={(value) => handleRuleFieldChange('template_id', value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                  <SelectValue placeholder="Velg mal" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {templates.filter(t => t.template_type === 'reminder').map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white">
                      {t.name}
                    </SelectItem>
                  ))}
                  {templates.filter(t => t.template_type !== 'reminder').map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white text-gray-400">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={ruleForm.is_active}
                onCheckedChange={(checked) => handleRuleFieldChange('is_active', checked)}
              />
              <Label className="text-gray-300">Aktiv</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleSaveRule} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Send Confirmation Modal */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-400">
              <AlertCircle className="w-5 h-5" />
              Bekreft utsendelse
            </DialogTitle>
          </DialogHeader>
          
          {campaignToSend && (
            <div className="py-4">
              <p className="text-gray-300">
                Er du sikker på at du vil sende kampanjen <strong>"{campaignToSend.name}"</strong> til <strong>{campaignToSend.recipient_count}</strong> mottakere?
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Denne handlingen kan ikke angres.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleSendCampaign} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send nå
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailCampaigns;
